/**
 * CREATIVE DETONATOR - 8-bit Sound Effects System
 * Retro game-style audio feedback
 *
 * Extracted from dashboard/core.js for reuse across app
 * @version 1.0
 */

// ============================================================================
// SOUND EFFECTS MODULE
// ============================================================================

export const Sounds = {
  context: null,
  enabled: true,

  /**
   * Initialize sound system
   * Loads user preference from localStorage
   */
  init() {
    const savedPref = localStorage.getItem('cd_sounds_enabled');
    this.enabled = savedPref !== 'false';
  },

  /**
   * Get or create AudioContext (lazy initialization)
   * @returns {AudioContext}
   */
  getContext() {
    if (!this.context) {
      this.context = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this.context;
  },

  /**
   * Play a sound effect
   * @param {string} type - Sound type: 'click', 'success', 'error', 'coin', 'hover'
   */
  play(type) {
    if (!this.enabled) return;

    try {
      const ctx = this.getContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      switch (type) {
        case 'click':
          oscillator.frequency.setValueAtTime(800, ctx.currentTime);
          oscillator.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.05);
          gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);
          oscillator.start(ctx.currentTime);
          oscillator.stop(ctx.currentTime + 0.05);
          break;

        case 'success':
          oscillator.frequency.setValueAtTime(523, ctx.currentTime);
          oscillator.frequency.setValueAtTime(659, ctx.currentTime + 0.1);
          oscillator.frequency.setValueAtTime(784, ctx.currentTime + 0.2);
          gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
          oscillator.start(ctx.currentTime);
          oscillator.stop(ctx.currentTime + 0.3);
          break;

        case 'error':
          oscillator.frequency.setValueAtTime(200, ctx.currentTime);
          oscillator.frequency.setValueAtTime(150, ctx.currentTime + 0.1);
          gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
          oscillator.start(ctx.currentTime);
          oscillator.stop(ctx.currentTime + 0.2);
          break;

        case 'coin':
          oscillator.frequency.setValueAtTime(988, ctx.currentTime);
          oscillator.frequency.setValueAtTime(1319, ctx.currentTime + 0.1);
          gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
          oscillator.start(ctx.currentTime);
          oscillator.stop(ctx.currentTime + 0.15);
          break;

        case 'hover':
          oscillator.frequency.setValueAtTime(440, ctx.currentTime);
          gainNode.gain.setValueAtTime(0.03, ctx.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.02);
          oscillator.start(ctx.currentTime);
          oscillator.stop(ctx.currentTime + 0.02);
          break;

        case 'deduct':
          // Legacy deduct - kept for compatibility but now more neutral
          oscillator.frequency.setValueAtTime(500, ctx.currentTime);
          oscillator.frequency.setValueAtTime(400, ctx.currentTime + 0.08);
          gainNode.gain.setValueAtTime(0.06, ctx.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.12);
          oscillator.start(ctx.currentTime);
          oscillator.stop(ctx.currentTime + 0.12);
          break;

        case 'coinSpend':
          // 8-bit coin collect - quick ascending blip (like Mario coin)
          oscillator.type = 'square';
          oscillator.frequency.setValueAtTime(800, ctx.currentTime);
          oscillator.frequency.setValueAtTime(1200, ctx.currentTime + 0.05);
          oscillator.frequency.setValueAtTime(1600, ctx.currentTime + 0.1);
          gainNode.gain.setValueAtTime(0.08, ctx.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.12);
          oscillator.start(ctx.currentTime);
          oscillator.stop(ctx.currentTime + 0.12);
          break;

        case 'cashRegister':
          // Cash register "cha-ching" for purchases
          oscillator.type = 'square';
          oscillator.frequency.setValueAtTime(1200, ctx.currentTime);
          oscillator.frequency.setValueAtTime(1600, ctx.currentTime + 0.08);
          oscillator.frequency.setValueAtTime(2000, ctx.currentTime + 0.16);
          gainNode.gain.setValueAtTime(0.08, ctx.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.25);
          oscillator.start(ctx.currentTime);
          oscillator.stop(ctx.currentTime + 0.25);
          break;
      }
    } catch (e) {
      // Silent fail - audio not critical
      console.debug('[Sounds] Playback failed:', e.message);
    }
  },

  /**
   * Toggle sound effects on/off
   * @param {boolean} enabled - Whether sounds are enabled
   */
  toggle(enabled) {
    this.enabled = enabled;
    localStorage.setItem('cd_sounds_enabled', enabled);
  },

  /**
   * Check if sounds are enabled
   * @returns {boolean}
   */
  isEnabled() {
    return this.enabled;
  }
};

// Global export for non-module contexts
if (typeof window !== 'undefined') {
  window.Sounds = Sounds;
}
